"use client";

import { formatINR, formatNumber } from "@/lib/calculators/rates";
import { ResultField } from "@/lib/tools/types";

interface ResultCardProps {
  field: ResultField;
  value: number | string | boolean;
}

/**
 * Display a single result value with appropriate formatting.
 * Primary results get brand-colored gradient background; others get muted.
 */
export function ResultCard({ field, value }: ResultCardProps) {
  const isPrimary = field.isPrimary ?? false;
  const bgColor = field.color || (isPrimary ? "bg-brand/10" : "bg-secondary");
  const textColor = field.color ? "text-foreground" : isPrimary ? "text-brand" : "text-foreground";

  let displayValue: string | number | boolean = value;

  if (typeof value === "boolean") {
    displayValue = value ? "Yes" : "No";
  } else if (typeof value === "number") {
    switch (field.format) {
      case "currency":
        displayValue = formatINR(value);
        break;
      case "percent":
        displayValue = `${formatNumber(value)}%`;
        break;
      case "number":
        displayValue = formatNumber(value);
        break;
      case "years":
        displayValue = `${formatNumber(Math.round(value))} ${value === 1 ? "year" : "years"}`;
        break;
      case "text":
      default:
        displayValue = String(value);
    }
  }

  return (
    <div
      className={`rounded-lg border border-border/50 p-4 sm:p-6 transition-all hover:border-border ${isPrimary ? "border-brand/30" : ""}`}
    >
      <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-2">
        {field.label}
      </p>
      <div
        className={`text-2xl sm:text-3xl font-bold ${textColor}`}
      >
        {displayValue}
      </div>
    </div>
  );
}
